import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  offset,
  shift,
  size,
  useHover,
  safePolygon,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useInteractions,
  useListItem,
  useListNavigation,
  useRole,
  useTypeahead,
  limitShift,
  Placement,
} from "@floating-ui/react";
import {
  createContext,
  Dispatch,
  forwardRef,
  HTMLProps,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
  FocusEvent,
  MouseEvent,
  AriaAttributes,
  ReactElement,
  useMemo,
} from "react";
import styles from "./MenuLegacy.module.scss";
import { MenuLegacyItem } from "./MenuLegacyItem";
import cx from "classnames";
import { Icon, IconProps } from "../Icon";
import Next from "@servicetitan/hammer-icon/mdi/round/navigate_next.svg";
import Up from "@servicetitan/hammer-icon/mdi/round/arrow_drop_up.svg";
import Down from "@servicetitan/hammer-icon/mdi/round/arrow_drop_down.svg";
import { Button, ButtonProps } from "../Button";
import { useMergeRefs } from "../../hooks";
import { ThemeProvider, ThemeProviderContext } from "../../providers";
import { DialogContext } from "../Dialog";
import { PortalProviderContext } from "../../providers/PortalProvider";
import { DrawerContext } from "../Drawer";

type PopoverControlProps = {
  disableAutoHeight?: boolean;
  root?: HTMLElement | React.MutableRefObject<HTMLElement | null> | null;
  placement?: "bottom-start" | "top-start" | "bottom-end" | "top-end";
};

export const MenuLegacyContext = createContext<
  {
    getItemProps: (
      userProps?: HTMLProps<HTMLElement>,
    ) => Record<string, unknown>;
    activeIndex: number | null;
    setActiveIndex?: Dispatch<SetStateAction<number | null>>;
    setHasFocusInside?: Dispatch<SetStateAction<boolean>>;
    isOpen: boolean;
  } & PopoverControlProps
>({
  getItemProps: () => ({}),
  activeIndex: null,
  isOpen: false,
});

type TriggerReturnProps = {
  "data-open"?: boolean;
  "data-focus-inside"?: boolean;
  "aria-controls"?: AriaAttributes["aria-controls"];
  "aria-expanded"?: AriaAttributes["aria-expanded"];
  "aria-haspopup"?: AriaAttributes["aria-haspopup"];
  id?: string;
  onClick?: () => void;
  onFocus?: () => void;
  onKeyDown?: () => void;
  onKeyUp?: () => void;
  onMouseDown?: () => void;
  onPointerDown?: () => void;
  ref?: ((instance: HTMLElement | null) => void) | null;
};

type DefaultTriggerProps = {
  trigger?: never;
  open?: boolean;
  icon?: IconProps["svg"];
} & (
  | {
      label: string;
    }
  | {
      "aria-label": string;
    }
  | {
      "aria-labellbedby": string;
    }
) &
  Omit<ButtonProps, "icon"> &
  PopoverControlProps;

type CustomTriggerProps = {
  trigger: (props: TriggerReturnProps) => ReactElement;
  icon?: IconProps["svg"];
  label?: never;
  open?: boolean;
  // This is to allow any type of element to be used as a custom trigger element
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Omit<HTMLProps<any>, "ref"> &
  PopoverControlProps;

export type MenuLegacyProps = DefaultTriggerProps | CustomTriggerProps;

export const MenuLegacyComponent = forwardRef<HTMLElement, MenuLegacyProps>(
  (
    {
      children,
      disableAutoHeight,
      open,
      icon,
      root,
      placement = "bottom-start",
      ...props
    },
    forwardedRef,
  ) => {
    const [isOpen, setIsOpen] = useState(!!open || false);
    const [hasFocusInside, setHasFocusInside] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [menuHeight, setMenuLegacyHeight] = useState<number | null>(null);
    const [arrowStart, setArrowStart] = useState(false);
    const [arrowEnd, setArrowEnd] = useState(false);

    const elementsRef = useRef<Array<HTMLButtonElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);
    const parent = useContext(MenuLegacyContext);
    const { root: portalRoot } = useContext(PortalProviderContext);

    const tree = useFloatingTree();
    const nodeId = useFloatingNodeId();
    const parentId = useFloatingParentNodeId();
    const item = useListItem();

    const isNested = parentId != null;

    const { theme, mode = "light" } = useContext(ThemeProviderContext);

    const dialogContext = useContext(DialogContext);
    const drawerContext = useContext(DrawerContext);

    const fallbackArr = () => {
      if (!isNested) {
        if (placement.startsWith("top")) {
          return disableAutoHeight
            ? placement
            : ["top-start", "top-end", "bottom-start", "bottom-end"];
        }
        return disableAutoHeight
          ? placement
          : ["bottom-start", "bottom-end", "top-start", "top-end"];
      }

      return parent.disableAutoHeight
        ? ["right-start"]
        : ["right-start", "right-end", "left-start", "left-end"];
    };

    const handleIsOpenChange = (internalOpen: boolean) => {
      if (typeof open === "boolean") {
        setIsOpen(open);
        return;
      }
      setIsOpen(internalOpen);
    };

    const { floatingStyles, refs, context } = useFloating<HTMLButtonElement>({
      nodeId,
      open: isOpen,
      onOpenChange: handleIsOpenChange,
      placement: isNested ? "right-start" : placement,
      middleware: [
        offset({
          mainAxis: isNested ? 0 : 4,
          alignmentAxis: isNested ? -4 : 0,
        }),
        flip({
          fallbackStrategy: "bestFit",
          fallbackPlacements: fallbackArr() as Placement[],
        }),
        shift({
          mainAxis: !parent.disableAutoHeight,
          crossAxis: false,
          limiter: limitShift({
            mainAxis: !parent.disableAutoHeight,
          }),
        }),
        size({
          padding: 8,
          apply({ availableWidth, availableHeight, elements }) {
            if (disableAutoHeight ?? parent.disableAutoHeight) return;
            setMenuLegacyHeight(availableHeight);
            Object.assign(elements.floating.style, {
              maxWidth: `${availableWidth}px`,
              maxHeight: `${availableHeight}px`,
            });
          },
        }),
      ],
      whileElementsMounted: autoUpdate,
    });
    const [hoverEnabled, setHoverEnabled] = useState(false);
    const hover = useHover(context, {
      enabled: hoverEnabled,
      delay: { open: 75 },
      handleClose: safePolygon({ blockPointerEvents: true }),
    });
    const click = useClick(context, {
      event: "mousedown",
      toggle: !isNested,
      ignoreMouse: isNested,
    });
    const role = useRole(context, { role: "menu" });
    const dismiss = useDismiss(context, { bubbles: true });
    const listNavigation = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      nested: isNested,
      onNavigate: setActiveIndex,
    });
    const typeahead = useTypeahead(context, {
      listRef: labelsRef,
      onMatch: isOpen ? setActiveIndex : undefined,
      activeIndex,
    });

    const { getReferenceProps, getFloatingProps, getItemProps } =
      useInteractions([hover, click, role, dismiss, listNavigation, typeahead]);

    // Event emitter allows you to communicate across tree components.
    // This effect closes all menus when an item gets clicked anywhere
    // in the tree.
    useEffect(() => {
      if (!tree) return;

      function handleTreeClick() {
        setIsOpen(!!open || false);
      }

      function onSubMenuLegacyOpen(event: {
        nodeId: string;
        parentId: string;
      }) {
        if (event.nodeId === nodeId || event.parentId !== parentId) {
          return;
        }
        setIsOpen(!!open || false);
      }

      tree.events.on("click", handleTreeClick);
      tree.events.on("menuopen", onSubMenuLegacyOpen);

      return () => {
        tree.events.off("click", handleTreeClick);
        tree.events.off("menuopen", onSubMenuLegacyOpen);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree, nodeId, parentId]);

    useEffect(() => {
      if (disableAutoHeight ?? parent.disableAutoHeight) return;
      if (!isOpen) {
        setMenuLegacyHeight(null);
        return;
      }
      setTimeout(() => {
        if (!scrollRef.current || !refs.floating.current) return;
        setMenuLegacyHeight(refs.floating.current.clientHeight);
        const isOverflow =
          scrollRef.current.scrollHeight > refs.floating.current.clientHeight;
        if (!isOverflow) return;
        const showTop = scrollRef.current.scrollTop > 0;
        const showBottom =
          scrollRef.current.scrollHeight -
            refs.floating.current.clientHeight -
            scrollTop -
            16 >
          0;
        setArrowStart(showTop);
        setArrowEnd(showBottom);
      }, 0);
    }, [
      disableAutoHeight,
      isOpen,
      parent.disableAutoHeight,
      refs.floating,
      scrollTop,
    ]);

    useEffect(() => {
      if (!isOpen || !tree) return;
      tree.events.emit("menuopen", { parentId, nodeId });
    }, [tree, isOpen, nodeId, parentId]);

    const menuComponentCX = cx({
      [styles["nested-menu"]]: isNested,
      [styles["item"]]: !isNested,
    });

    const menuRefs = useMergeRefs([refs.setReference, item.ref, forwardedRef]);

    const triggerProps: TriggerReturnProps = {
      "data-open": isOpen,
      "data-focus-inside": hasFocusInside,
      ref: menuRefs,
      ...getReferenceProps(
        parent.getItemProps({
          ...props,
          // This is to allow any type of element to be used as a custom trigger element
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onFocus(event: FocusEvent<any>) {
            props.onFocus?.(event);
            setHasFocusInside(false);
            parent.setHasFocusInside?.(true);
          },
          // This is to allow any type of element to be used as a custom trigger element
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as HTMLProps<any>),
      ),
    };

    const value = useMemo(
      () => ({
        activeIndex,
        setActiveIndex,
        getItemProps,
        setHasFocusInside,
        isOpen,
        disableAutoHeight: disableAutoHeight ?? parent.disableAutoHeight,
      }),
      [
        activeIndex,
        disableAutoHeight,
        getItemProps,
        isOpen,
        parent.disableAutoHeight,
      ],
    );

    return (
      <FloatingNode id={nodeId}>
        {isNested ? (
          <button
            ref={menuRefs}
            tabIndex={parent.activeIndex === item.index ? 0 : -1}
            role="menuitem"
            data-anv="menu-item"
            data-open={isOpen ? "" : undefined}
            data-nested=""
            data-focus-inside={hasFocusInside ? "" : undefined}
            className={menuComponentCX}
            {...getReferenceProps(
              parent.getItemProps({
                ...props,
                onFocus(event: FocusEvent<HTMLButtonElement>) {
                  props.onFocus?.(event);
                  setHasFocusInside(false);
                  parent.setHasFocusInside?.(true);
                },
                onMouseEnter: () => setHoverEnabled(true),
              } as HTMLProps<HTMLButtonElement>),
            )}
          >
            {icon ? <Icon inherit aria-hidden svg={icon} /> : null}
            {"label" in props && props.label}
            <div style={{ flexGrow: 1 }} />
            <Icon aria-hidden svg={Next} />
          </button>
        ) : "trigger" in props ? (
          props.trigger?.(triggerProps)
        ) : (
          <Button
            {...(props as ButtonProps)}
            {...(triggerProps as DefaultTriggerProps)}
            {...getReferenceProps({
              onFocus: (event) => props.onFocus?.(event),
            } as HTMLProps<HTMLButtonElement>)}
            icon={icon}
            data-anv="menu"
          >
            {"label" in props && props.label}
          </Button>
        )}

        <MenuLegacyContext.Provider value={value}>
          <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
            {isOpen &&
              (dialogContext == null ||
                dialogContext?.ref?.current != null) && (
                <FloatingPortal
                  root={
                    root ??
                    dialogContext?.ref?.current ??
                    drawerContext?.ref?.current ??
                    portalRoot
                  }
                >
                  <FloatingFocusManager
                    context={context}
                    modal={false}
                    initialFocus={isNested ? -1 : 0}
                    returnFocus={!isNested}
                  >
                    <ThemeProvider theme={theme} mode={mode}>
                      <div
                        ref={refs.setFloating}
                        className={styles["menu"]}
                        style={floatingStyles}
                        {...getFloatingProps({
                          onMouseEnter: () => {
                            setHoverEnabled(false);
                          },
                          onKeyDown: (e) => {
                            if (
                              e.code === "ArrowLeft" ||
                              e.code === "ArrowRight"
                            ) {
                              e.stopPropagation();
                            }
                          },
                        })}
                      >
                        {arrowStart && (
                          <div className={styles["arrow-up"]} aria-hidden>
                            <Icon
                              svg={Up}
                              size="xlarge"
                              className={styles["arrow-icon"]}
                            />
                          </div>
                        )}
                        <div
                          ref={scrollRef}
                          className={styles["scroll"]}
                          style={{
                            position: "relative",
                            maxHeight: menuHeight ?? "inherit",
                          }}
                          onScroll={(e: MouseEvent<HTMLDivElement>) =>
                            setScrollTop(e.currentTarget.scrollTop)
                          }
                        >
                          {children}
                        </div>
                        {arrowEnd && (
                          <div className={styles["arrow-down"]} aria-hidden>
                            <Icon
                              svg={Down}
                              size="xlarge"
                              className={styles["arrow-icon"]}
                            />
                          </div>
                        )}
                      </div>
                    </ThemeProvider>
                  </FloatingFocusManager>
                </FloatingPortal>
              )}
          </FloatingList>
        </MenuLegacyContext.Provider>
      </FloatingNode>
    );
  },
);

MenuLegacyComponent.displayName = "MenuLegacyComponent";

const MenuLegacyElement = forwardRef<HTMLElement, MenuLegacyProps>(
  (props, ref) => {
    const parentId = useFloatingParentNodeId();

    if (parentId === null) {
      return (
        <FloatingTree>
          <MenuLegacyComponent {...props} ref={ref} />
        </FloatingTree>
      );
    }

    return <MenuLegacyComponent {...props} ref={ref} />;
  },
);

MenuLegacyElement.displayName = "MenuLegacy";

export const MenuLegacy = Object.assign(MenuLegacyElement, {
  Item: MenuLegacyItem,
});
